/**
 * Lane CAMERA-PITCH (user 10-11 "the camera follows the character like 9yin"): an optional yaw follow. The camera
 * always stays centred on the player (renderer.ts; bouts too, duelFraming.ts only zooms out). This file adds, behind
 * the `cameraFollow` setting (default off: the 9yin way is movement relative to the camera and orbiting by hand,
 * docs/research/2026-10-11-camera-view.md §9), a slow swing that brings a click-to-move walk's heading up the screen
 * once the player has left the camera alone for a moment. Keyboard walks never swing it (they are screen-relative,
 * so a swing would chase its own tail). No drawing; tested in Node.
 */

/** Seconds after the last camera drag or key turn before the follow starts. */
export const FOLLOW_IDLE = 1.2;
/** Fastest swing (quarter turns a second: about 40°/s). */
const FOLLOW_RATE = 0.45;
/** Only a heading within this of up-screen (radians) is followed; sideways and backward walks are left alone. */
const FOLLOW_CONE = (75 * Math.PI) / 180;

export interface FollowTurn {
  readonly busy: boolean;
  follow(dq: number): void;
}

export class CameraFollow {
  private idle = 0;

  /**
   * `heading`: the walk's direction in the turned view (iso.dirTurn of the walk, before the squash), or null when
   * not on a click-to-move path. Returns the quarter turns swung this frame.
   */
  step(dt: number, heading: readonly [number, number] | null, turn: FollowTurn, on: boolean): number {
    if (turn.busy) this.idle = 0;
    else this.idle += dt;
    if (!on || !heading || this.idle < FOLLOW_IDLE) return 0;
    const [a, b] = heading;
    // The turned view's axes on screen: x = a − b, y = a + b (down). Angle clockwise from up-screen:
    const th = Math.atan2(a - b, -(a + b));
    if (!Number.isFinite(th) || Math.abs(th) > FOLLOW_CONE || Math.abs(th) < 0.02) return 0;
    // A turn of the camera by +δ turns everything on screen clockwise by δ: swing by −θ, rate-limited.
    const want = -th / (Math.PI / 2);
    const max = FOLLOW_RATE * dt;
    const dq = Math.max(-max, Math.min(max, want * Math.min(1, 2.5 * dt)));
    turn.follow(dq);
    return dq;
  }
}
